import React, { useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AddEditAlertPhoneNumberForm from './AddEditAlertPhoneNumberForm';
import AddEditAlertEmailForm from './AddEditAlertEmailForm';
import Overlay from '../../shared/Overlay';
import { FormattedMessage } from 'react-intl';
import {
  editAlertDevice,
  assignAlertDeviceToAlertGroup,
  loadAlertDeviceDetails,
} from '../../../redux/actions/alertDeviceActions';
import { progressStepStatuses } from '../../shared/progressIndicator/ProgressIndicatorItem';
import ProgressIndicator from '../../shared/progressIndicator/ProgressIndicator';
import { Helmet } from 'react-helmet-async';
import AssociateAlertGroup from './AssociateAlertGroup';
import permissions from '../../../permissionsList';
import AccessControl from '../../../AccessControl';

export function UnconnectedEditAlertDevice(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceDetails, id, dispatch } = props;
  
  // More defensive access to location state
  const locationState = location?.state || {};
  const locationSearch = location?.search || '';
  const defaultPrevious = id ? `/devices/alert/${id}/view/` : '/devices/';
  const previousRef = useRef(locationState.previous || defaultPrevious);
  const previous = previousRef.current;

  // Load device details when component mounts or ID changes
  useEffect(() => {
    if (id) {
      dispatch(loadAlertDeviceDetails(id));
    }
  }, [id, dispatch]);
  
  // Early return if deviceDetails is not available
  if (!deviceDetails) {
    return null;
  }

  const searchParams = new URLSearchParams(locationSearch);
  const mode = searchParams.get('mode');
  const next = searchParams.get('next');
  const prev = searchParams.get('past');

  let nextSteps = [];

  if (next) {
    nextSteps = next.split(',');
  }
  let prevSteps = [];
  if (prev) {
    prevSteps = prev.split(',');
  }

  const formSteps = {
    type: 'users',
    nextSteps,
    prevSteps: [...prevSteps, mode],
    id: deviceDetails.alert_device_id,
    previous,
  };

  function progressDisplay() {
    if (prevSteps.length > 0 || nextSteps.length > 0) {
      const prevStepsArr = prevSteps.map((item, index) => {
        if (index === 0) {
          return {
            title: item,
            first: true,
            status: progressStepStatuses.complete,
          };
        } else {
          return { title: item, status: progressStepStatuses.complete };
        }
      });
      const nextStepsArr = nextSteps.map((item, index) => {
        if (index === nextSteps.length - 1) {
          return { title: item, last: true };
        } else {
          return { title: item };
        }
      });

      let currentFirst = false;
      let currentLast = false;
      if (nextSteps.length === 0) {
        currentLast = true;
      }
      if (prevSteps.length === 0) {
        currentFirst = true;
      }

      const steps = [
        ...prevStepsArr,
        {
          status: progressStepStatuses.active,
          title: mode,
          first: currentFirst,
          last: currentLast,
        },
        ...nextStepsArr,
      ];

      return (
        <React.Fragment>
          <ProgressIndicator steps={steps} data-test="progress-bar" />
          <hr />
        </React.Fragment>
      );
    } else {
      return '';
    }
  }

  const editAlertPhoneTitle = (
    <FormattedMessage
      id="editAlertDevice.addAlertPhoneTitle"
      defaultMessage="Edit Alert Details for Phone Number: {device}"
      description="The title for the page to edit a unit"
      values={{
        device: deviceDetails.phone_number,
      }}>
      {(txt) => <h1 className="no-margin text-deep-blue text-center">{txt}</h1>}
    </FormattedMessage>
  );
  const editAlertEmailTitle = (
    <FormattedMessage
      id="editAlertDevice.addAlertEmailTitle"
      defaultMessage="Edit Alert Details for Email: {device}"
      description="The title for the page to edit a unit"
      values={{
        device: deviceDetails.email_address,
      }}>
      {(txt) => <h1 className="no-margin text-deep-blue text-center">{txt}</h1>}
    </FormattedMessage>
  );
  const associateAlertGroupText = (
    <FormattedMessage
      id="editAlertDevice.associateAlertGroupText"
      defaultMessage="Associate Alert Group for: {device}"
      description="The title for the page to edit a unit"
      values={{
        device: deviceDetails.email_address || deviceDetails.phone_number,
      }}>
      {(txt) => <h1 className="no-margin text-deep-blue text-center">{txt}</h1>}
    </FormattedMessage>
  );

  function deviceEditDisplay() {
    if (mode !== null) {
      switch (mode) {
        case 'associateAlertGroup':
          return (
            <React.Fragment>
              {associateAlertGroupText}

              <hr />
              <AssociateAlertGroup
                deviceDetails={deviceDetails}
                previous={previous}
                onSubmit={associateAlertGroupsSubmit}
                data-test="edit-device-type-3"
              />
            </React.Fragment>
          );

        default:
          break;
      }
    } else {
      switch (deviceDetails.alert_device_type) {
        case 3:
          return (
            <React.Fragment>
              {editAlertPhoneTitle}

              <hr />
              <AddEditAlertPhoneNumberForm
                deviceDetails={deviceDetails}
                previous={previous}
                onSubmit={editDeviceSubmit}
                data-test="edit-device-type-3"
              />
            </React.Fragment>
          );
        case 24:
          return (
            <React.Fragment>
              {editAlertPhoneTitle}

              <hr />
              <AddEditAlertPhoneNumberForm
                deviceDetails={deviceDetails}
                previous={previous}
                onSubmit={editDeviceSubmit}
                data-test="edit-device-type-24"
              />
            </React.Fragment>
          );
        case 4:
          return (
            <React.Fragment>
              {editAlertEmailTitle}

              <hr />
              <AddEditAlertEmailForm
                deviceDetails={deviceDetails}
                previous={previous}
                onSubmit={editDeviceSubmit}
                data-test="edit-device-type-4"
              />
            </React.Fragment>
          );
        case 25:
          return (
            <React.Fragment>
              {editAlertEmailTitle}
              <hr />
              <AddEditAlertEmailForm
                deviceDetails={deviceDetails}
                previous={previous}
                onSubmit={editDeviceSubmit}
                data-test="edit-device-type-25"
              />
            </React.Fragment>
          );
        default:
          break;
      }
    }
  }

  // submit functions

  function editDeviceSubmit(values) {
    dispatch(editAlertDevice(values, navigate, previous));
  }

  function associateAlertGroupsSubmit(values) {
    dispatch(
      assignAlertDeviceToAlertGroup(values, navigate, previous, formSteps)
    );
  }

  return (
    <Overlay rootUrl={previous} canClose={true} data-test="edit-alert-device">
      <FormattedMessage
        id="alertDeviceEdit.browserTabInfo"
        defaultMessage="Alert Devices | Editing {name}"
        description="Done button"
        values={{
          name: deviceDetails.alert_device_description,
        }}>
        {(txt) => (
          <Helmet>
            <title>{txt}</title>
          </Helmet>
        )}
      </FormattedMessage>
      <div className="content-container content-container--column">
        {progressDisplay()}
        <AccessControl
          data-test="access-control"
          feature={permissions.static.devices.alert.editAlertDevice}
          permissionMessage={true}>
          {deviceEditDisplay()}
        </AccessControl>
      </div>
    </Overlay>
  );
}
const mapStateToProps = (state, ownProps) => {
  const id = ownProps.id;
  const alertDeviceDetails = state.devices.alertDeviceDetails;
  const deviceDetails = alertDeviceDetails[id];

  return {
    deviceDetails,
  };
};

const ConnectedEditAlertDevice = connect(mapStateToProps)(UnconnectedEditAlertDevice);

function EditAlertDeviceWrapper() {
  const { id } = useParams();
  return <ConnectedEditAlertDevice id={id} />;
}

export default EditAlertDeviceWrapper;
